import { PlusOutlined } from '@ant-design/icons';
import { Code, ConnectError } from '@bufbuild/connect';
import { Button, Input, notification, Select, Tabs } from 'antd';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BASE_URI, LocalStorageKey } from '@/shared/config';
import { useAppDispatch, useAppSelector, useSearch } from '@/shared/hooks';
import { Layout } from '@/shared/ui';

import { authModel } from '@/entities/auth';
import { userModel } from '@/entities/user';

import { Footer } from '@/widgets/footer';
import { Header } from '@/widgets/header';

import {
  ORGANIZATION_KIND_ALL,
  ORGANIZATION_KIND_SELECT_VALUES,
  organizationKindMessages,
  OrganizationKindSelectValue,
  TabKey,
} from './config';
import styles from './Home.module.scss';
import {
  CreateAccountModal,
  OrganizationsTab,
  SystemLoadTab,
  UsersTab,
} from './ui';
import type { CreateAccountSubmitPayload } from './ui/CreateAccountModal';

const { Search } = Input;

export const Home: FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { search, setSearch, debouncedSearch } = useSearch();

  const hasAdminBasicAccess = useAppSelector(
    authModel.selectors.selectHasAdminBasicAccess,
  );
  const canViewSystemLoad = useAppSelector(
    authModel.selectors.selectCanViewSystemLoad,
  );
  const canManageDSO = useAppSelector(userModel.selectors.selectCanManageDSO);

  const [activeTab, setActiveTab] = useState<TabKey>(
    () =>
      (localStorage.getItem(LocalStorageKey.homeActiveTab) as TabKey) ||
      'organizations',
  );
  const [organizationKindFilter, setOrganizationKindFilter] =
    useState<OrganizationKindSelectValue>(ORGANIZATION_KIND_ALL);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);

  const organizationKindOptions = useMemo(
    () =>
      ORGANIZATION_KIND_SELECT_VALUES.map((value) => ({
        value,
        label: formatMessage(organizationKindMessages[value]),
      })),
    [formatMessage],
  );

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as TabKey);
    localStorage.setItem(LocalStorageKey.homeActiveTab, key);
  };

  const openCreateAccountModal = () => {
    setIsCreateAccountModalOpen(true);
  };

  const closeCreateAccountModal = () => {
    setIsCreateAccountModalOpen(false);
  };

  const handleCreateAccountSubmit = async (
    payload: CreateAccountSubmitPayload,
  ) => {
    if (payload.variant === 'signUp') {
      window.open(`${BASE_URI}/sign-up`, '_blank');
      closeCreateAccountModal();

      return;
    }

    try {
      setIsCreatingAccount(true);

      await dispatch(
        userModel.thunks.createOrganization({
          Name: payload.organizationName,
          Type: payload.organizationType,
          Affiliation: payload.dsoParentID
            ? { case: 'DSOParentID', value: payload.dsoParentID }
            : undefined,
          Owner: {
            case: 'NewOwnerData',
            value: {
              Email: payload.ownerEmail,
              FirstName: payload.ownerFirstName,
              LastName: payload.ownerLastName,
              Language: payload.ownerLanguage,
              Country: payload.ownerCountry,
            },
          },
        }),
      ).unwrap();

      closeCreateAccountModal();
    } catch (error) {
      // The modal already blocks a known duplicate, so this covers the cases its
      // lookup cannot see: a race, or an organization outside the actor's scope.
      const isOwnerEmailTaken =
        error instanceof ConnectError && error.code === Code.AlreadyExists;

      notification.error({
        message: isOwnerEmailTaken
          ? formatMessage({
              id: 'createAccountModal.ownerEmail.organizationExists',
              defaultMessage:
                'An organization with this owner email already exists',
            })
          : error?.message,
      });
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const tabItems = [
    {
      key: 'organizations',
      label: (
        <FormattedMessage
          id="home.tabs.organizations"
          defaultMessage="Organizations"
        />
      ),
      children: (
        <OrganizationsTab
          debouncedSearch={debouncedSearch}
          filterCase={
            organizationKindFilter === ORGANIZATION_KIND_ALL
              ? undefined
              : organizationKindFilter
          }
        />
      ),
    },
    ...(hasAdminBasicAccess
      ? [
          {
            key: 'users',
            label: (
              <FormattedMessage id="home.tabs.users" defaultMessage="Users" />
            ),
            children: <UsersTab debouncedSearch={debouncedSearch} />,
          },
        ]
      : []),
    ...(canViewSystemLoad
      ? [
          {
            key: 'systemLoad',
            label: (
              <FormattedMessage
                id="home.tabs.systemLoad"
                defaultMessage="System Load"
              />
            ),
            // mount only while active so opening the tab always fetches a
            // fresh system load state (antd keeps visited panes mounted
            // otherwise)
            children: activeTab === 'systemLoad' ? <SystemLoadTab /> : null,
          },
        ]
      : []),
  ];

  return (
    <Layout>
      <Header />

      <Layout.Content innerClassName={styles.container}>
        <div className={styles.pageHeader}>
          <Search
            className={styles.search}
            placeholder={formatMessage({
              id: 'home.search',
              defaultMessage: 'Search by name, email or country',
            })}
            value={search}
            onChange={handleChangeSearch}
          />

          {hasAdminBasicAccess && (
            <Button
              type="primary"
              icon={<PlusOutlined rev={undefined} />}
              onClick={openCreateAccountModal}
            >
              {formatMessage({
                id: 'users.createAccount',
                defaultMessage: 'Create Account',
              })}
            </Button>
          )}
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          tabBarExtraContent={
            activeTab === 'organizations' && hasAdminBasicAccess ? (
              <Select<OrganizationKindSelectValue>
                className={styles.organizationKindSelect}
                value={organizationKindFilter}
                onChange={setOrganizationKindFilter}
                options={organizationKindOptions}
              />
            ) : null
          }
        />
      </Layout.Content>

      <Footer />

      <CreateAccountModal
        open={isCreateAccountModalOpen}
        canManageDSO={canManageDSO}
        loading={isCreatingAccount}
        onCancel={closeCreateAccountModal}
        onSubmit={handleCreateAccountSubmit}
      />
    </Layout>
  );
};
